/*
PROV: COHINT439.BF.GROUP_ADMIN_CONSENT_UI
REQ: Think-Platinum/cohorta#439
WHY: Self-contained PECR consent UI for the group-admin portal — the writer side of
     the consent contract whose reader (src/lib/consent.ts) and GA4 loader gate
     (src/lib/gaAnalytics.ts) already exist here. Mirrors the marketing site's
     ConsentManager (cohorta-website); operators authenticate via TOTP cookies,
     not Firebase, so consent is device-local only (no server mirror). All writes
     go through src/lib/consent.ts, so saving fires cohorta:consent-changed and
     the loader gate reacts with no reload. See docs/consent-contract.md, ADR-046.
     Copy: Nicole to review.
*/

import { useMemo, useState, useSyncExternalStore } from "react";
import {
  parseStoredConsent,
  onConsentChange,
  saveConsent,
  GRANT_NONE,
  CONSENT_STORAGE_KEY,
} from "../lib/consent";

// Only the categories Cohorta actually uses are shown. The published cookie
// policy declares essential + analytics only — Cohorta sets no functional or
// marketing cookies — so those rows are omitted. The stored/wire schema still
// carries all three category keys (contract-conformant per ADR-046); the two
// unused ones stay false. Re-add an entry here if a category comes into use.
const CATEGORIES = [
  {
    key: "essential",
    title: "Essential",
    alwaysOn: true,
    description:
      "Strictly necessary — signing in, security, and remembering your consent choice. Always on.",
    cookies: ["Session & security", "Your consent choice"],
  },
  {
    key: "analytics",
    title: "Analytics",
    description:
      "Helps us understand how the admin portal is used so we can improve it. First-party only; never sold.",
    cookies: ["Google Analytics (page views)"],
  },
];

// "Accept all" grants only the categories offered above (analytics only).
// Deliberately not GRANT_ALL — matches the website and member-app surfaces.
const ACCEPT_ALL_CATEGORIES = { functional: false, analytics: true, marketing: false };

const PANEL = "rounded-lg border border-gray-200 bg-white shadow-xl";

const subscribeConsent = (cb) => onConsentChange(cb);
const getRawConsent = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(CONSENT_STORAGE_KEY) : null;
  } catch {
    return null;
  }
};
const nullSnapshot = () => null;
const noopSubscribe = () => () => {};
const trueSnapshot = () => true;
const falseSnapshot = () => false;

export function ConsentManager({ privacyPolicyUrl = "https://www.cohorta.me/legal/cookies/" }) {
  const mounted = useSyncExternalStore(noopSubscribe, trueSnapshot, falseSnapshot);
  const raw = useSyncExternalStore(subscribeConsent, getRawConsent, nullSnapshot);
  const record = useMemo(() => parseStoredConsent(raw), [raw]);

  const [prefsOpen, setPrefsOpen] = useState(false);
  const [cats, setCats] = useState(GRANT_NONE);

  const needsPrompt = !record;

  const openPrefs = () => {
    setCats(record?.categories ?? GRANT_NONE);
    setPrefsOpen(true);
  };
  const acceptAll = () => {
    saveConsent(ACCEPT_ALL_CATEGORIES, "accept_all");
    setPrefsOpen(false);
  };
  const rejectAll = () => {
    saveConsent(GRANT_NONE, "reject_all");
    setPrefsOpen(false);
  };
  const savePrefs = () => {
    saveConsent(cats, "preference_centre");
    setPrefsOpen(false);
  };
  const toggle = (key) => setCats((c) => ({ ...c, [key]: !c[key] }));

  if (!mounted) return null;

  return (
    <>
      {needsPrompt && !prefsOpen && (
        <div
          data-testid="consent-banner"
          role="region"
          aria-label="Cookie consent"
          className="fixed inset-x-0 bottom-0 z-[100] border-t border-gray-200 bg-white/95 p-4 shadow-lg backdrop-blur"
        >
          <div className="mx-auto flex max-w-4xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-800">
              We use essential cookies to run the admin portal, and — only with your consent —
              analytics to improve it.{" "}
              {privacyPolicyUrl && (
                <a href={privacyPolicyUrl} className="font-medium underline underline-offset-2">
                  Privacy &amp; cookies
                </a>
              )}
            </p>
            <div className="flex shrink-0 flex-wrap gap-2">
              <button
                type="button"
                data-testid="consent-manage"
                onClick={openPrefs}
                className="rounded-md border border-gray-300 bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-200"
              >
                Manage preferences
              </button>
              <button
                type="button"
                data-testid="consent-reject-all"
                onClick={rejectAll}
                className="rounded-md border border-gray-400 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Reject all
              </button>
              <button
                type="button"
                data-testid="consent-accept-all"
                onClick={acceptAll}
                className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-700"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      {!needsPrompt && (
        <button
          type="button"
          data-testid="consent-open-prefs"
          onClick={openPrefs}
          className="fixed bottom-3 left-3 z-[90] rounded-full border border-gray-300 bg-white/90 px-3 py-1 text-xs font-medium text-gray-600 shadow hover:bg-white"
        >
          Cookie preferences
        </button>
      )}

      {prefsOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setPrefsOpen(false)}
        >
          <div
            data-testid="consent-prefs"
            role="dialog"
            aria-modal="true"
            aria-label="Cookie preferences"
            className={`${PANEL} w-[min(34rem,100%)] p-6`}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900">Cookie preferences</h2>
            <p className="mt-1 text-sm text-gray-600">
              Choose which non-essential cookies the admin portal may use. You can change this any time.
            </p>

            <div className="mt-4 max-h-[50vh] space-y-4 overflow-y-auto pr-1">
              {CATEGORIES.map((cat) => {
                const checked = cat.alwaysOn ? true : cats[cat.key];
                return (
                  <div key={cat.key} className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {cat.title}
                        {cat.alwaysOn && (
                          <span className="ml-2 text-xs font-normal text-gray-500">Always on</span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-gray-600">{cat.description}</p>
                      <ul className="mt-1 list-disc pl-4 text-xs text-gray-500">
                        {cat.cookies.map((c) => (
                          <li key={c}>{c}</li>
                        ))}
                      </ul>
                    </div>
                    <label className="pt-0.5">
                      <input
                        type="checkbox"
                        role="switch"
                        data-testid={`consent-toggle-${cat.key}`}
                        checked={checked}
                        disabled={cat.alwaysOn}
                        aria-label={cat.title}
                        onChange={cat.alwaysOn ? undefined : () => toggle(cat.key)}
                        className="h-5 w-5 accent-gray-900 disabled:opacity-50"
                      />
                    </label>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="consent-prefs-reject-all"
                onClick={rejectAll}
                className="rounded-md border border-gray-400 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Reject all
              </button>
              <button
                type="button"
                data-testid="consent-prefs-accept-all"
                onClick={acceptAll}
                className="rounded-md border border-gray-300 bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-200"
              >
                Accept all
              </button>
              <button
                type="button"
                data-testid="consent-save"
                onClick={savePrefs}
                className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-700"
              >
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
